// ============================================================
// FROM BEYOND, DRAWN (plan body-map T2; the user's pick, "in love with",
// 2026-10-09): a window to the void. The figure is pitch black - a hole in
// space with a faint cold rim - and through it a sky (engine/plate/auras
// `voidSky`) fixed in space while the hole moves: the hole is drawn inside
// the figure, so it sways and is struck with it, and each frame the plate's
// clock pins the sky back (`pinVoid`: the inverse of whatever moved the
// hole, measured against the figure's unmoving frame - the bench's own way).
// ============================================================

import { OutlineFilter } from './Outline';
import { voidSky } from '../../../../engine/plate/auras';
import type { Box } from '../../../../engine/plate/frame';
import tx from '../../../../styles/PlateTraits.module.css';
import { MASK_RECT } from '../conditions';

/** The sky's colours: three nebulae and four star tints (tokens, in order). */
const NEBULA = [tx.voidNebula0, tx.voidNebula1, tx.voidNebula2];
const STAR = [tx.voidStar0, tx.voidStar1, tx.voidStar2, tx.voidStar3];

/** The window's mask (the silhouette), its rim in the health band's colour, and the nebulae's soft drift. */
export function VoidDefs({ u }: { u: string }) {
  return (
    <>
      <mask id={`${u}-vw`} maskUnits="userSpaceOnUse" {...MASK_RECT}>
        <use href={`#${u}-sil`} className={tx.maskWhite} />
      </mask>
      {/* The rim is the figure's only line, so it carries the health ramp: wider and whole (plan plate-unboxed). */}
      <OutlineFilter id={`${u}-vr`} radius={14} flood={tx.voidRimFlood} />
      <filter id={`${u}-vn`} x="-30%" y="-30%" width="160%" height="160%">
        <feTurbulence type="fractalNoise" baseFrequency={0.004} numOctaves={4} seed={21} result="n" />
        <feDisplacementMap
          in="SourceGraphic"
          in2="n"
          scale={420}
          xChannelSelector="R"
          yChannelSelector="G"
          result="d"
        />
        <feGaussianBlur in="d" stdDeviation={40} />
      </filter>
    </>
  );
}

/** What shows through: nebulae (the black must win), a tilted galaxy, a cluster, a field of stars. */
function Sky({ u, box }: { u: string; box: Box }) {
  const sky = voidSky(box);
  const glow = `url(#${u}-tglow)`;
  return (
    <g data-void-sky>
      <g filter={`url(#${u}-vn)`}>
        {sky.nebulae.map((n) => (
          <ellipse
            key={n.tint}
            className={NEBULA[n.tint]}
            cx={n.cx}
            cy={n.cy}
            rx={n.rx}
            ry={n.ry}
            opacity={n.opacity}
          />
        ))}
      </g>
      <g transform={`translate(${sky.galaxy.x} ${sky.galaxy.y}) rotate(-28) scale(1 0.42)`}>
        <circle className={tx.voidCore} r={46} filter={glow} />
        {sky.galaxy.stars.map((s, k) => (
          <circle key={k} className={STAR[s.tint]} cx={s.x} cy={s.y} r={s.r} opacity={s.opacity} />
        ))}
      </g>
      {[...sky.cluster, ...sky.stars].map((s, k) => (
        <circle
          key={k}
          className={STAR[s.tint]}
          cx={s.x}
          cy={s.y}
          r={s.r}
          filter={'big' in s && s.big ? glow : undefined}
        />
      ))}
    </g>
  );
}

/** On the figure itself: the hole, its sky through the silhouette, and its faint cold rim. */
export function VoidHole({ u, box }: { u: string; box: Box }) {
  return (
    <g pointerEvents="none" data-trait="beyond">
      <use href={`#${u}-sil`} className={tx.voidBlack} />
      <g mask={`url(#${u}-vw)`}>
        <Sky u={u} box={box} />
      </g>
      <g>
        <use href={`#${u}-sil`} className={tx.voidBlack} filter={`url(#${u}-vr)`} />
      </g>
    </g>
  );
}

/** The matrix calls the pin needs, as the DOM gives them (absent where there is no layout, as under jsdom). */
interface Matrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  inverse(): Matrix;
  multiply(other: Matrix): Matrix;
}
type Placed = { getCTM?: () => Matrix | null; parentNode: unknown };

/**
 * Pin the sky in space: give it the inverse of everything that has moved the hole since the figure's unmoving frame
 * (`<u>-figure`, which nothing transforms). Called by the plate's clock each frame; `reset` puts it back.
 */
export function pinVoid(root: SVGSVGElement, u: string, reset = false): void {
  const sky = root.querySelector('[data-void-sky]');
  if (!sky) return;
  if (reset) return sky.removeAttribute('transform');
  const moved = (sky.parentNode as Placed | null)?.getCTM?.();
  const still = (root.querySelector(`[id="${u}-figure"]`) as unknown as Placed | null)?.getCTM?.();
  if (!moved || !still) return;
  const m = moved.inverse().multiply(still);
  const next = `matrix(${[m.a, m.b, m.c, m.d, m.e, m.f].map((v) => v.toFixed(4)).join(' ')})`;
  // An unchanged write still re-runs every filter over the sky's hundreds of stars.
  if (sky.getAttribute('transform') !== next) sky.setAttribute('transform', next);
}
